import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Trash2,
  Plus,
  Phone,
  Mail,
  MapPin,
  Building,
  Linkedin,
  Twitter,
  Calendar,
  MessageSquare,
  Users,
  FileText,
  Upload,
  Video,
  X
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import {
  InlineField,
  InlineTextarea,
  InlineSelect,
  InlineSlider,
  InlineArrayField,
  InlineTagEditor
} from '../components/InlineField';
import InteractionForm from '../components/InteractionForm';
import ConnectionForm from '../components/ConnectionForm';
import { format, formatDistanceToNow } from 'date-fns';

const INTERACTION_ICONS = {
  call: Phone,
  video_call: Video,
  meeting: Calendar,
  email: Mail,
  text: MessageSquare,
  social_media: MessageSquare,
  coffee: Calendar,
  event: Calendar,
  other: MessageSquare
};

const CATEGORY_OPTIONS = [
  { value: 'family', label: 'Family', color: '#ef4444' },
  { value: 'friends', label: 'Friends', color: '#22c55e' },
  { value: 'professional', label: 'Professional', color: '#3b82f6' },
  { value: 'acquaintance', label: 'Acquaintance', color: '#6b7280' }
];

function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    fetchContact,
    updateContact,
    deleteContact,
    fetchInteractions,
    createInteraction,
    deleteInteraction,
    fetchConnections,
    createConnection,
    deleteConnection,
    fetchDocuments,
    uploadDocument,
    tags,
    fetchTags
  } = useApp();

  const [contact, setContact] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [connections, setConnections] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('interactions');
  const [showInteractionForm, setShowInteractionForm] = useState(false);
  const [showConnectionForm, setShowConnectionForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [uploading, setUploading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    const [contactData, interactionsData, connectionsData, docsData] = await Promise.all([
      fetchContact(id),
      fetchInteractions(id),
      fetchConnections(id),
      fetchDocuments(id)
    ]);
    setContact(contactData);
    setInteractions(interactionsData);
    setConnections(connectionsData);
    setDocuments(docsData);
    setLoading(false);
  }, [id]); // Only depend on id, fetch functions are stable

  useEffect(() => {
    loadData();
  }, [id]); // Only reload when id changes

  useEffect(() => {
    fetchTags();
  }, []); // Fetch tags once on mount

  // Inline field save handler — accepts (field, value) or (dataObject)
  const handleFieldSave = async (fieldOrData, value) => {
    const data = typeof fieldOrData === 'object' && fieldOrData !== null && !Array.isArray(fieldOrData)
      ? fieldOrData
      : { [fieldOrData]: value };

    try {
      await updateContact(id, data);
      setContact(prev => ({ ...prev, ...data }));
    } catch (error) {
      console.error('Error saving field:', error);
      const fresh = await fetchContact(id);
      setContact(fresh);
      throw error;
    }
  };

  const handleDelete = async () => {
    await deleteContact(id);
    navigate('/contacts');
  };

  const handleAddInteraction = async (data) => {
    await createInteraction(data);
    loadData();
  };

  const handleDeleteInteraction = async (interactionId) => {
    if (window.confirm('Delete this interaction?')) {
      await deleteInteraction(interactionId);
      loadData();
    }
  };

  const handleAddConnection = async (data) => {
    await createConnection(data);
    loadData();
  };

  const handleDeleteConnection = async (connectionId) => {
    if (window.confirm('Remove this connection?')) {
      await deleteConnection(connectionId);
      loadData();
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      await uploadDocument(file, id, null, file.name);
      loadData();
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="empty-state">
        <p>Contact not found</p>
        <Link to="/contacts" className="btn btn-primary mt-3">
          Back to Contacts
        </Link>
      </div>
    );
  }

  const getCategoryColor = (category) => {
    const opt = CATEGORY_OPTIONS.find(o => o.value === category);
    return opt ? opt.color : '#6b7280';
  };

  const getStrengthColor = (strength) => {
    if (strength >= 70) return 'var(--success)';
    if (strength >= 40) return 'var(--warning)';
    return 'var(--danger)';
  };

  // Normalize emails/phones for InlineArrayField
  const normalizedEmails = contact.emails?.length > 0
    ? contact.emails
    : (contact.email ? [{ value: contact.email, type: 'personal' }] : []);

  const normalizedPhones = contact.phones?.length > 0
    ? contact.phones
    : (contact.phone ? [{ value: contact.phone, type: 'mobile' }] : []);

  const existingConnectionIds = connections.map(c => c.connected_contact?.id);

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <button
          className="btn btn-ghost"
          onClick={() => navigate('/contacts')}
          style={{ marginBottom: '1rem' }}
        >
          <ArrowLeft size={18} />
          Back to Contacts
        </button>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '1rem',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <div className="avatar avatar-lg">
              {contact.first_name[0]}{contact.last_name?.[0] || ''}
            </div>
            <div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline', marginBottom: '0.25rem' }}>
                <InlineField
                  value={contact.first_name}
                  field="first_name"
                  type="text"
                  placeholder="First name"
                  onSave={handleFieldSave}
                  displayStyle={{ fontSize: '1.5rem', fontWeight: '700' }}
                />
                <InlineField
                  value={contact.last_name}
                  field="last_name"
                  type="text"
                  placeholder="Last name"
                  onSave={handleFieldSave}
                  displayStyle={{ fontSize: '1.5rem', fontWeight: '700' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <InlineSelect
                  value={contact.category}
                  field="category"
                  options={CATEGORY_OPTIONS}
                  onSave={handleFieldSave}
                />
                <InlineTagEditor
                  selectedTags={contact.tags || []}
                  allTags={tags}
                  onSave={(tagIds) => handleFieldSave('tags', tagIds)}
                />
              </div>
            </div>
          </div>
          <div className="contact-header-actions">
            <button className="btn btn-danger" onClick={() => setShowDeleteConfirm(true)}>
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      </div>

      <div className="contact-detail-layout">
        {/* Sidebar */}
        <div className="contact-detail-sidebar">
          {/* Contact Info */}
          <div className="card">
            <h3 className="card-title" style={{ marginBottom: '1rem' }}>Contact Info</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <InlineArrayField
                values={normalizedEmails}
                field="emails"
                legacyField="email"
                icon={Mail}
                typeOptions={['personal', 'work', 'other']}
                inputType="email"
                placeholder="Add email..."
                linkPrefix="mailto:"
                onSave={handleFieldSave}
              />
              <InlineArrayField
                values={normalizedPhones}
                field="phones"
                legacyField="phone"
                icon={Phone}
                typeOptions={['mobile', 'home', 'work', 'other']}
                inputType="tel"
                placeholder="Add phone..."
                linkPrefix="tel:"
                onSave={handleFieldSave}
              />
              <InlineField
                value={contact.company}
                field="company"
                icon={Building}
                type="text"
                placeholder="Add company..."
                onSave={handleFieldSave}
              />
              <InlineField
                value={contact.job_title || contact.position}
                field="job_title"
                icon={Building}
                type="text"
                placeholder="Add job title..."
                onSave={handleFieldSave}
                displayStyle={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}
              />
              <InlineField
                value={contact.address}
                field="address"
                icon={MapPin}
                type="text"
                placeholder="Add address..."
                onSave={handleFieldSave}
              />
              <InlineField
                value={contact.birthday}
                field="birthday"
                icon={Calendar}
                type="date"
                placeholder="Add birthday..."
                onSave={handleFieldSave}
                formatDisplay={(v) => {
                  try { return format(new Date(v), 'MMMM d, yyyy'); }
                  catch { return v; }
                }}
              />
              <InlineField
                value={contact.linkedin_url || contact.url}
                field="linkedin_url"
                icon={Linkedin}
                type="url"
                placeholder="Add LinkedIn URL..."
                onSave={handleFieldSave}
                formatDisplay={() => 'LinkedIn Profile'}
                linkHref={contact.linkedin_url || contact.url}
              />
              {/* LinkedIn connected date shown indented under LinkedIn */}
              {(contact.linkedin_url || contact.url || contact.linkedin_connected_date) && (
                <InlineField
                  value={contact.linkedin_connected_date}
                  field="linkedin_connected_date"
                  type="date"
                  placeholder="Add connected date..."
                  onSave={handleFieldSave}
                  formatDisplay={(v) => {
                    try { return `Connected ${format(new Date(v), 'MMM d, yyyy')}`; }
                    catch { return `Connected ${v}`; }
                  }}
                  displayStyle={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginLeft: '1.75rem' }}
                />
              )}
              <InlineField
                value={contact.twitter_handle}
                field="twitter_handle"
                icon={Twitter}
                type="text"
                placeholder="Add Twitter handle..."
                onSave={handleFieldSave}
              />
            </div>
          </div>

          {/* Relationship */}
          <div className="card">
            <h3 className="card-title" style={{ marginBottom: '1rem' }}>Relationship</h3>
            <InlineSlider
              value={contact.relationship_strength}
              field="relationship_strength"
              onSave={handleFieldSave}
            />
            <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="text-sm text-muted">Contact Goal</span>
                <InlineField
                  value={contact.contact_frequency_goal}
                  field="contact_frequency_goal"
                  type="number"
                  placeholder="30"
                  onSave={handleFieldSave}
                  formatDisplay={(v) => `Every ${v} days`}
                  displayStyle={{ fontSize: '0.875rem' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span className="text-sm text-muted">Last Contact</span>
                <span className="text-sm">
                  {contact.last_contacted
                    ? formatDistanceToNow(new Date(contact.last_contacted), { addSuffix: true })
                    : 'Never'}
                </span>
              </div>
            </div>
          </div>

          {/* Context — always visible */}
          <div className="card">
            <h3 className="card-title" style={{ marginBottom: '1rem' }}>Context</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div>
                <div className="text-sm text-muted" style={{ marginBottom: '0.25rem' }}>How We Met</div>
                <InlineField
                  value={contact.how_we_met}
                  field="how_we_met"
                  type="text"
                  placeholder="Add how you met..."
                  onSave={handleFieldSave}
                />
              </div>
              <div>
                <div className="text-sm text-muted" style={{ marginBottom: '0.25rem' }}>Interests</div>
                <InlineField
                  value={contact.interests}
                  field="interests"
                  type="text"
                  placeholder="Add interests..."
                  onSave={handleFieldSave}
                />
              </div>
              <div>
                <div className="text-sm text-muted" style={{ marginBottom: '0.25rem' }}>Relationship Context</div>
                <InlineTextarea
                  value={contact.relationship_context}
                  field="relationship_context"
                  placeholder="Add relationship context..."
                  onSave={handleFieldSave}
                />
              </div>
              <div>
                <div className="text-sm text-muted" style={{ marginBottom: '0.25rem' }}>Notes</div>
                <InlineTextarea
                  value={contact.notes}
                  field="notes"
                  placeholder="Add notes..."
                  onSave={handleFieldSave}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div>
          {/* Tabs */}
          <div style={{
            display: 'flex',
            gap: '0.25rem',
            borderBottom: '1px solid var(--gray-200)',
            marginBottom: '1.5rem'
          }}>
            {['interactions', 'connections', 'documents'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '0.75rem 1rem',
                  border: 'none',
                  background: 'transparent',
                  borderBottom: '2px solid',
                  borderColor: activeTab === tab ? 'var(--primary)' : 'transparent',
                  color: activeTab === tab ? 'var(--primary)' : 'var(--gray-500)',
                  fontWeight: '500',
                  textTransform: 'capitalize',
                  cursor: 'pointer'
                }}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Interactions Tab */}
          {activeTab === 'interactions' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                <button className="btn btn-primary" onClick={() => setShowInteractionForm(true)}>
                  <Plus size={16} />
                  Log Interaction
                </button>
              </div>
              {interactions.length === 0 ? (
                <div className="card empty-state">
                  <MessageSquare size={32} className="empty-state-icon" />
                  <p>No interactions logged yet</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {interactions.map(interaction => {
                    const Icon = INTERACTION_ICONS[interaction.type] || MessageSquare;
                    return (
                      <div key={interaction.id} className="card">
                        <div style={{ display: 'flex', gap: '1rem' }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '10px',
                            background: 'var(--gray-100)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            <Icon size={20} className="text-muted" />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <div>
                                <div className="font-medium" style={{ textTransform: 'capitalize' }}>
                                  {interaction.type.replace('_', ' ')}
                                </div>
                                <div className="text-sm text-muted">
                                  {format(new Date(interaction.date), 'MMMM d, yyyy')}
                                  {interaction.duration_minutes && ` \u2022 ${interaction.duration_minutes} min`}
                                  {interaction.location && ` \u2022 ${interaction.location}`}
                                </div>
                              </div>
                              <button
                                className="btn btn-ghost btn-sm"
                                onClick={() => handleDeleteInteraction(interaction.id)}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                            {interaction.subject && (
                              <div style={{ marginTop: '0.5rem', fontWeight: '500' }}>
                                {interaction.subject}
                              </div>
                            )}
                            {interaction.notes && (
                              <div style={{ marginTop: '0.5rem' }} className="text-sm">
                                {interaction.notes}
                              </div>
                            )}
                            {interaction.sentiment && (
                              <div style={{ marginTop: '0.5rem' }}>
                                <span
                                  className="badge"
                                  style={{
                                    background: interaction.sentiment === 'positive' ? 'rgba(34, 197, 94, 0.1)' :
                                               interaction.sentiment === 'negative' ? 'rgba(239, 68, 68, 0.1)' :
                                               'var(--gray-100)',
                                    color: interaction.sentiment === 'positive' ? 'var(--success)' :
                                           interaction.sentiment === 'negative' ? 'var(--danger)' :
                                           'var(--gray-500)',
                                    textTransform: 'capitalize'
                                  }}
                                >
                                  {interaction.sentiment}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Connections Tab */}
          {activeTab === 'connections' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                <button className="btn btn-primary" onClick={() => setShowConnectionForm(true)}>
                  <Plus size={16} />
                  Add Connection
                </button>
              </div>
              {connections.length === 0 ? (
                <div className="card empty-state">
                  <Users size={32} className="empty-state-icon" />
                  <p>No connections mapped yet</p>
                </div>
              ) : (
                <div className="grid grid-2">
                  {connections.map(connection => (
                    <div key={connection.id} className="card" style={{ position: 'relative' }}>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => handleDeleteConnection(connection.id)}
                        style={{ position: 'absolute', top: '0.5rem', right: '0.5rem' }}
                      >
                        <X size={14} />
                      </button>
                      <Link
                        to={`/contacts/${connection.connected_contact?.id}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          textDecoration: 'none',
                          color: 'inherit'
                        }}
                      >
                        <div className="avatar">
                          {connection.connected_contact?.first_name?.[0] || '?'}
                          {connection.connected_contact?.last_name?.[0] || ''}
                        </div>
                        <div>
                          <div className="font-medium">
                            {connection.connected_contact?.first_name} {connection.connected_contact?.last_name}
                          </div>
                          {connection.relationship_type && (
                            <div className="text-sm text-muted">{connection.relationship_type}</div>
                          )}
                        </div>
                      </Link>
                      {connection.notes && (
                        <div className="text-sm mt-2" style={{ paddingLeft: '52px' }}>
                          {connection.notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                <label className="btn btn-primary" style={{ cursor: 'pointer' }}>
                  <Upload size={16} />
                  {uploading ? 'Uploading...' : 'Upload Document'}
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                    accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif"
                    disabled={uploading}
                  />
                </label>
              </div>
              {documents.length === 0 ? (
                <div className="card empty-state">
                  <FileText size={32} className="empty-state-icon" />
                  <p>No documents uploaded yet</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {documents.map(doc => (
                    <div key={doc.id} className="card" style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <FileText size={20} className="text-muted" />
                        <div style={{ flex: 1 }}>
                          <div className="font-medium">{doc.original_name}</div>
                          <div className="text-xs text-muted">
                            {format(new Date(doc.created_at), 'MMM d, yyyy')}
                            {doc.size && ` \u2022 ${(doc.size / 1024).toFixed(1)} KB`}
                          </div>
                        </div>
                        <a
                          href={`/api/documents/${doc.id}/download`}
                          className="btn btn-secondary btn-sm"
                        >
                          Download
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showInteractionForm && (
        <InteractionForm
          contactId={id}
          onClose={() => setShowInteractionForm(false)}
          onSave={handleAddInteraction}
        />
      )}

      {showConnectionForm && (
        <ConnectionForm
          contactId={id}
          existingConnectionIds={existingConnectionIds}
          onClose={() => setShowConnectionForm(false)}
          onSave={handleAddConnection}
        />
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Delete Contact</h2>
              <button className="btn btn-ghost" onClick={() => setShowDeleteConfirm(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete {contact.first_name} {contact.last_name}?</p>
              <p className="text-sm text-muted mt-2">
                This will also delete all interactions and connections associated with this contact.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={handleDelete}>
                Delete Contact
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .contact-detail-layout {
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 1.5rem;
        }
        .contact-detail-sidebar {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        .contact-header-actions {
          display: flex;
          gap: 0.5rem;
        }
        .inline-field {
          padding: 0.25rem 0.375rem;
          border-radius: 0.375rem;
          transition: background 0.15s;
          margin: -0.25rem -0.375rem;
          cursor: pointer;
        }
        .inline-field:hover,
        .inline-field:active {
          background: var(--gray-100);
        }
        .inline-field-placeholder {
          color: var(--gray-400);
          font-style: italic;
          font-size: 0.875rem;
        }
        @media (max-width: 768px) {
          .contact-detail-layout {
            grid-template-columns: 1fr;
          }
          .contact-detail-sidebar {
            order: 2;
          }
        }
      `}</style>
    </div>
  );
}

export default ContactDetail;
